import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useApiMutation from 'hooks/useApiMutation';
import PreferenceConfirmation from 'views/shared/MgmtFeePolicyEnforcer/preferenceConfirmation';
import { Button, Dropdown } from '../../../components';
import { DisplayField, DisplayFieldType, DisplaySection } from '../../../components/DisplayForms';
import DisplayForm from '../../../components/DisplayForms/DisplayForm';
import { DropdownItem } from '../../../components/Dropdown';
import { logError } from '../../../components/LogError';
import { AppContext } from '../../../context/ContextProvider';
import { getFieldAttributes } from '../../../helpers';
import { buildDisplayText, capitalizeFirstLetter, toInternalId } from '../../../utils';
import { SubjectOptionKeys } from '../types';
import { ContactUsResult, contactUsApi } from '../services/contactUsApi';
import CustomInput from 'components/CustomInput';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { GbpToTargetCurrency } from 'types/commonTypes';

const ddlConfig = {
  itemWrapperStyle: { width: '100%' },
  containerClassName: 'w-full',
  itemsContainerClassName:
    'max-h-[300px] overflow-y-auto w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg',
  itemClassName:
    'py-3 px-4 text-sm flex hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 cursor-pointer transition-colors',
  className:
    'flex-1 text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap p-3 justify-start border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-500 transition-colors',
};

enum DisplayTextKeys {
  CONTACT_METHOD_TEXT = 'contact-method-text',
  CONTACT_METHOD_PLACEHOLDER_TEXT = 'contact-method-placeholder-text',
  TITLE_TEXT = 'title_text',
  SUBTITLE_TEXT = 'subTitle_text',
  CONTACT_TEXT = 'contact-text',
  SUBJECT_TEXT = 'subject-text',
  WITHDRAW_FUNDS_TEXT = 'withdraw-funds',
  MESSAGE_TEXT = 'message-text',
  SEND_REQUEST_TEXT = 'send-message',
  SEND_REQUEST_SUCCESS_TEXT = 'send-request-success-text',
  SEND_REQUEST_ERROR_TEXT = 'send-request-error-text',

  FINANCIAL = 'financial',
  GENERAL = 'general',
  RELATION_MGR = 'relationMgr',
  SUPPORT = 'support',
  UPDATE_DETAILS = 'updateDetails',
  WITHDRAWAL_REQUEST = 'withdrawalRequest',
}

enum ContactMethodType {
  EMAIL = 'email',
  PHONE = 'phone-call',
}

enum PaymentTab {
  UK = 0,
  INTERNATIONAL = 1,
}

const errorText = {
  amountRequired: 'Amount must be greater than 0',
  amountExceedsBalance: 'Amount cannot be greater than balance',
  accountNameRequired: 'Account name must be entered',
  fieldRequired: 'This field is required',
  messageRequired: 'Message must be entered',
};

enum ModelKeys {
  CONTACT_METHOD = 'contactMethod',
  CONTACT = 'contact',
  SUBJECT = 'subject',
  MESSAGE = 'message',
  AMOUNT = 'amount',
  BIC = 'bic',
  IBAN = 'iban',
  SORTCODE = 'sortCode',
  WITH_DRAW_FUNDS_FORM = 'WithDrawFundsFound',
  ACCOUNT_NAME = 'accountName',
  ACCOUNT_NUMBER = 'accountNumber',
}

const contactMethodOptions = (t: TFunction) => {
  return Object.values(ContactMethodType).map((option) => {
    const text = capitalizeFirstLetter(t(`account:contactUs:${option}`));
    const icon =
      option === ContactMethodType.EMAIL ? (
        <svg
          className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ) : (
        <svg
          className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      );
    return {
      id: toInternalId(option),
      value: option,
      text,
      content: (
        <div className="flex items-center text-sm">
          {icon}
          <span>{text}</span>
        </div>
      ),
    };
  });
};

const getSubjectIcon = (option: SubjectOptionKeys) => {
  const iconClass = 'w-4 h-4 mr-2 text-gray-500 dark:text-gray-400';
  switch (option) {
    case SubjectOptionKeys.FINANCIAL:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case SubjectOptionKeys.SUPPORT:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      );
    case SubjectOptionKeys.UPDATE_DETAILS:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      );
    case SubjectOptionKeys.RELATION_MGR:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
};

const getSubjectOptions = (displayText: Record<string, string>) => {
  return Object.values(SubjectOptionKeys).map((option) => {
    const text = capitalizeFirstLetter(displayText[option] || option);
    return {
      id: toInternalId(option),
      value: option,
      text,
      content: (
        <div className="flex items-center text-sm">
          {getSubjectIcon(option)}
          <span>{text}</span>
        </div>
      ),
    };
  });
};

const TextBoxInput = ({
  setModel,
  type,
  fieldKey,
  model,
  validate,
  error,
  setErrors,
  labelText,
  placeHolderText,
  className,
  disabled,
  gbpToTargetCurrency,
}: {
  disabled?: boolean;
  className?: string;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  setModel: React.Dispatch<React.SetStateAction<ModelType>>;
  type: DisplayFieldType;
  fieldKey: ModelKeys;
  model: ModelType;
  labelText: string;
  placeHolderText?: string;
  gbpToTargetCurrency?: GbpToTargetCurrency;
  error?: string | null;
  validate: (text?: string | number) => string | null;
}) => {
  const runValidation = (value?: string | number) => {
    setErrors((prev) => ({ ...prev, [fieldKey]: validate(value) }));
  };

  const onFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string | number) => {
    const nextValue = `${value ?? e.target.value}`;
    setModel((prev) => ({ ...prev, [fieldKey]: nextValue }));
    // Re-run validation while an error is on screen so it clears as soon as the value becomes valid.
    if (error) runValidation(nextValue);
  };

  return (
    <div className="flex flex-col relative">
      {labelText && <p className="text-sm mb-1">{labelText}</p>}
      <div className="w-[200px]">
        <CustomInput
          disabled={disabled}
          className={className}
          type={type}
          inputClassName="text-sm border border-b-0 py-2 px-1"
          placeholder={placeHolderText}
          {...(type === DisplayFieldType.CURRENCY
            ? {
                formatter: {
                  format(value) {
                    return gbpToTargetCurrency?.convertAndFormat?.(value) ?? '';
                  },
                },
              }
            : {})}
          // onClear={() => onClear(field)}
          aria-label={`${fieldKey}`}
          value={model[fieldKey]}
          onChange={onFieldUpdate}
          name={fieldKey}
          onBlur={(_, val) => {
            runValidation(val);
          }}
        />
        {error && <p className="text-xs text-red absolute bottom-[-15px] h-[15px]">{error}</p>}
      </div>
    </div>
  );
};

type ValidationErrors = Partial<Record<ModelKeys, string | null>>;

interface ModelType {
  [ModelKeys.SUBJECT]: string;
  [ModelKeys.MESSAGE]: string;
  [ModelKeys.CONTACT]: string | number;
  [ModelKeys.CONTACT_METHOD]: string;

  [ModelKeys.AMOUNT]: string;
  [ModelKeys.BIC]: string;
  [ModelKeys.IBAN]: string;
  [ModelKeys.SORTCODE]: string;
  [ModelKeys.WITH_DRAW_FUNDS_FORM]: string;

  [ModelKeys.ACCOUNT_NAME]: string;
  [ModelKeys.ACCOUNT_NUMBER]: string;
}

const defaultModel: ModelType = {
  [ModelKeys.SUBJECT]: SubjectOptionKeys.GENERAL,
  [ModelKeys.MESSAGE]: '',
  [ModelKeys.CONTACT]: '',
  [ModelKeys.CONTACT_METHOD]: ContactMethodType.EMAIL,
  [ModelKeys.AMOUNT]: '',
  [ModelKeys.BIC]: '',
  [ModelKeys.IBAN]: '',
  [ModelKeys.SORTCODE]: '',
  [ModelKeys.WITH_DRAW_FUNDS_FORM]: '',
  [ModelKeys.ACCOUNT_NAME]: '',
  [ModelKeys.ACCOUNT_NUMBER]: '',
};

const ContactUs = () => {
  const { t } = useTranslation();
  const { state: locationParam } = useLocation();
  const {
    state: { settings: language },
    formatter,
    gbpToTargetCurrency,
  } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const { portfolioBalances, loading: loadingBalances } = usePortfolioBalances();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:contactUs', t), [t]);
  const contactOptions = useMemo(() => contactMethodOptions(t), [t]);
  const subjectOptions = useMemo(() => getSubjectOptions(displayText), [displayText]);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [tab, setTab] = useState<PaymentTab>(PaymentTab.UK);

  const [popUpModel, setPopUpModel] = useState(false);
  const {
    executor: sendRequest,
    data: sendRequestResult,
    status: sendingRequest,
    error: errorSendingRequest,
  } = useApiMutation(contactUsApi);

  const [model, setModel] = useState<ModelType>({ ...defaultModel });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const availableBalance = useMemo(() => {
    return portfolioBalances?.[0]?.balance ?? 0;
  }, [portfolioBalances]);

  const isWithdrawal = model[ModelKeys.SUBJECT] === SubjectOptionKeys.WITHDRAWAL_REQUEST;

  const validateField = useCallback(
    (key: ModelKeys, value?: string | number): string | null => {
      switch (key) {
        case ModelKeys.AMOUNT: {
          const amount = Number(value);
          if (!value || !(amount > 0)) return errorText.amountRequired;
          if (amount > availableBalance) return errorText.amountExceedsBalance;
          return null;
        }
        case ModelKeys.ACCOUNT_NAME:
          return value ? null : errorText.accountNameRequired;
        case ModelKeys.ACCOUNT_NUMBER:
          return !value && tab === PaymentTab.UK ? errorText.fieldRequired : null;
        case ModelKeys.BIC:
        case ModelKeys.IBAN:
          return !value && tab === PaymentTab.INTERNATIONAL ? errorText.fieldRequired : null;
        case ModelKeys.MESSAGE:
          return value ? null : errorText.messageRequired;
        default:
          return null;
      }
    },
    [availableBalance, tab],
  );

  // Fields the CTA validates on click - the withdrawal form replaces the message with payment details.
  const getRequiredFields = (): ModelKeys[] => {
    if (!isWithdrawal) return [ModelKeys.MESSAGE];
    return [
      ModelKeys.AMOUNT,
      ModelKeys.ACCOUNT_NAME,
      ...(tab === PaymentTab.UK ? [ModelKeys.ACCOUNT_NUMBER] : [ModelKeys.BIC, ModelKeys.IBAN]),
    ];
  };

  const onSendRequest = () => {
    const validationErrors = getRequiredFields().reduce<ValidationErrors>(
      (acc, key) => ({ ...acc, [key]: validateField(key, model[key]) }),
      {},
    );

    if (Object.values(validationErrors).some((message) => !!message)) {
      setErrors(validationErrors);
      return;
    }

    setIsSendingRequest(true);
    sendRequest({
      subject: model[ModelKeys.SUBJECT],
      message: model[ModelKeys.MESSAGE],
      contactMethod: model[ModelKeys.CONTACT_METHOD],
      contactMethodValue: `${model[ModelKeys.CONTACT]}`,
      amount: model[ModelKeys.AMOUNT],
      bic: model[ModelKeys.BIC],
      iban: model[ModelKeys.IBAN],
      sortCode: model[ModelKeys.SORTCODE],
      accountName: model[ModelKeys.ACCOUNT_NAME],
      accountNumber: model[ModelKeys.ACCOUNT_NUMBER],
      currencyType: language.currency,
    }).catch(() => null);

    setErrors({});
    setModel({ ...defaultModel });
  };

  const updateModel = (key: string, value: unknown) => {
    setModel({ ...model, [key]: value });
  };
  const sections: DisplaySection[] = useMemo(() => {
    const contactField = 'CONTACT';
    const messageField = 'MESSAGE';
    const fields: DisplayField[] = [
      {
        ...getFieldAttributes(ModelKeys.SUBJECT, ModelKeys.SUBJECT, displayText[DisplayTextKeys.SUBJECT_TEXT]),
        type: DisplayFieldType.CUSTOM,
        customTemplate: () => {
          return (
            <Dropdown
              placeholder="Select item"
              value={model[ModelKeys.SUBJECT]}
              valueTemplate={
                <div className="flex w-[95%]">
                  <span className="truncate block">
                    {subjectOptions.find((x) => x.value === model[ModelKeys.SUBJECT])!.text}
                  </span>
                </div>
              }
              onItemSelect={(item: DropdownItem) => {
                setModel({ ...model, [ModelKeys.SUBJECT]: item.value });
                setErrors({});
              }}
              items={subjectOptions}
              {...ddlConfig}
            />
          );
        },
      },
      {
        ...getFieldAttributes(
          ModelKeys.WITH_DRAW_FUNDS_FORM,
          ModelKeys.WITH_DRAW_FUNDS_FORM,
          displayText[DisplayTextKeys.WITHDRAW_FUNDS_TEXT],
        ),
        type: DisplayFieldType.CUSTOM,
        customTemplate: () => {
          return (
            <div className="flex flex-col max-w-[558px]">
              <div className="flex justify-between items-center">
                <div className="w-[200px]">
                  <TextBoxInput
                    disabled={availableBalance <= 0}
                    setErrors={setErrors}
                    error={errors[ModelKeys.AMOUNT]}
                    placeHolderText="Insert amount"
                    fieldKey={ModelKeys.AMOUNT}
                    gbpToTargetCurrency={gbpToTargetCurrency}
                    type={DisplayFieldType.CURRENCY}
                    labelText="Insert amount"
                    model={model}
                    setModel={setModel}
                    validate={(val) => validateField(ModelKeys.AMOUNT, val)}
                  />
                </div>
                <div className="flex flex-col items-end">
                  Available to withdraw
                  <p>{availableBalance > 0 ? formatter.format(availableBalance) : formatter.format(0)}</p>
                </div>
              </div>

              <div className="w-full border-b-[1px] my-6" />

              <p className="">Payment details</p>

              <div className="flex">
                <Button
                  onClick={() => {
                    setTab(PaymentTab.UK);
                  }}
                  className="flex-1 h-[47px] text-center flex justify-center items-center !outline-none rounded-none focus:outline-none focus:ring-0 "
                  style={{ borderBottom: tab === PaymentTab.UK ? '3px solid #FF906D' : '1px solid #FF906D' }}
                >
                  Uk
                </Button>

                <Button
                  onClick={() => {
                    setTab(PaymentTab.INTERNATIONAL);
                  }}
                  className="flex-1 h-[47px] text-center flex justify-center items-center !outline-none rounded-none focus:outline-none focus:ring-0"
                  style={{ borderBottom: tab === PaymentTab.INTERNATIONAL ? '3px solid #FF906D' : '1px solid #FF906D' }}
                >
                  International
                </Button>
              </div>

              <div>
                <div className="flex items-center gap-4 flex-wrap pt-5">
                  <TextBoxInput
                    setErrors={setErrors}
                    error={errors[ModelKeys.ACCOUNT_NAME]}
                    className="flex-1 flex"
                    placeHolderText="Name on account"
                    fieldKey={ModelKeys.ACCOUNT_NAME}
                    type={DisplayFieldType.TEXT}
                    labelText="Name on account"
                    model={model}
                    setModel={setModel}
                    validate={(val) => validateField(ModelKeys.ACCOUNT_NAME, val)}
                  />

                  <TextBoxInput
                    setErrors={setErrors}
                    error={errors[ModelKeys.ACCOUNT_NUMBER]}
                    placeHolderText="Account Number"
                    fieldKey={ModelKeys.ACCOUNT_NUMBER}
                    type={DisplayFieldType.NUMERIC}
                    labelText="Account Number"
                    model={model}
                    setModel={setModel}
                    validate={(val) => validateField(ModelKeys.ACCOUNT_NUMBER, val)}
                  />

                  {tab === PaymentTab.INTERNATIONAL && (
                    <>
                      <TextBoxInput
                        setErrors={setErrors}
                        error={errors[ModelKeys.BIC]}
                        placeHolderText="BIC / SWIFT"
                        fieldKey={ModelKeys.BIC}
                        type={DisplayFieldType.TEXT}
                        labelText="BIC / SWIFT"
                        model={model}
                        setModel={setModel}
                        validate={(val) => validateField(ModelKeys.BIC, val)}
                      />

                      <TextBoxInput
                        setErrors={setErrors}
                        error={errors[ModelKeys.IBAN]}
                        placeHolderText="IBAN"
                        fieldKey={ModelKeys.IBAN}
                        type={DisplayFieldType.TEXT}
                        labelText="IBAN"
                        model={model}
                        setModel={setModel}
                        validate={(val) => validateField(ModelKeys.IBAN, val)}
                      />
                    </>
                  )}

                  <TextBoxInput
                    setErrors={setErrors}
                    error={errors[ModelKeys.SORTCODE]}
                    placeHolderText="Branch Code / Bank Code / Routing Number"
                    fieldKey={ModelKeys.SORTCODE}
                    type={DisplayFieldType.TEXT}
                    labelText="Branch Code / Bank Code / Routing Number"
                    model={model}
                    setModel={setModel}
                    validate={(val) => validateField(ModelKeys.SORTCODE, val)}
                  />
                </div>
              </div>
            </div>
          );
        },
      },
      {
        ...getFieldAttributes(
          messageField,
          ModelKeys[`${messageField}` as const],
          displayText[DisplayTextKeys[`${messageField}_TEXT` as const]],
        ),
        type: DisplayFieldType.TEXT_AREA,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
          updateModel(field?.modelKey || '', e.target.value);
          if (errors[ModelKeys.MESSAGE]) {
            setErrors((prev) => ({ ...prev, [ModelKeys.MESSAGE]: validateField(ModelKeys.MESSAGE, e.target.value) }));
          }
        },
        helperText: errors[ModelKeys.MESSAGE] || undefined,
        className: 'min-h-[160px]',
      },

      {
        ...getFieldAttributes(
          ModelKeys.CONTACT_METHOD,
          ModelKeys.CONTACT_METHOD,
          displayText[DisplayTextKeys.CONTACT_METHOD_TEXT],
        ),
        type: DisplayFieldType.CUSTOM,
        customTemplate: () => {
          return (
            <Dropdown
              placeholder="Select item"
              value={model[ModelKeys.CONTACT_METHOD]}
              valueTemplate={
                <div className="flex w-[95%]">
                  <span className="truncate block">
                    {contactOptions.find((x) => x.value === model[ModelKeys.CONTACT_METHOD])!.text}
                  </span>
                </div>
              }
              onItemSelect={(item: DropdownItem) => {
                setModel({ ...model, [ModelKeys.CONTACT_METHOD]: item.value, [ModelKeys.CONTACT]: '' });
              }}
              items={contactOptions}
              {...ddlConfig}
            />
          );
        },
      },

      {
        ...getFieldAttributes(
          contactField,
          ModelKeys[`${contactField}` as const],
          displayText[DisplayTextKeys[`${contactField}_TEXT` as const]],
        ),
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
          updateModel(field?.modelKey || '', e.target.value);
        },
        type:
          model[ModelKeys.CONTACT_METHOD] === ContactMethodType.PHONE ? DisplayFieldType.PHONE : DisplayFieldType.TEXT,
      },
    ];

    return [
      {
        className: 'w-full  gap-5',
        fields: [...fields].filter((x) => isWithdrawal || x.id !== ModelKeys.WITH_DRAW_FUNDS_FORM),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, tab, gbpToTargetCurrency, errors, availableBalance, validateField]);

  if (errorSendingRequest) {
    logError(JSON.stringify(errorSendingRequest));
  }

  useEffect(() => {
    if (isSendingRequest && sendingRequest === 'error') {
      setIsSendingRequest(false);
      enqueueSnackbar(displayText[DisplayTextKeys.SEND_REQUEST_ERROR_TEXT]);
    }

    if (isSendingRequest && sendingRequest === 'success') {
      setIsSendingRequest(false);
      if (!errorSendingRequest) {
        const result = sendRequestResult?.data as ContactUsResult;

        const isSuccess = result.isSuccess;
        if (isSuccess) {
          setModel((prev) => ({ ...prev, message: '' }));
          if (model[ModelKeys.SUBJECT] === SubjectOptionKeys.FINANCIAL) return setPopUpModel(true);
        }

        enqueueSnackbar(
          result.isSuccess
            ? displayText[DisplayTextKeys.SEND_REQUEST_SUCCESS_TEXT]
            : displayText[DisplayTextKeys.SEND_REQUEST_ERROR_TEXT],
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendingRequest]);

  useEffect(() => {
    setModel({
      ...model,
      [ModelKeys.SUBJECT]:
        locationParam && locationParam?.subject && locationParam.subject !== model[ModelKeys.SUBJECT]
          ? locationParam.subject
          : SubjectOptionKeys.GENERAL,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParam?.subject, language]);

  return (
    <div
      className={`flex flex-col p-5 bg-gray-50 dark:bg-slate-900 min-h-full ${
        isSendingRequest ? 'opacity-30 pointer-events-none' : ''
      }`}
    >
      {popUpModel && (
        <PreferenceConfirmation
          body={<div className="text-gray-700 dark:text-gray-300">{t('common:requestText')}</div>}
          show={popUpModel}
          footer={
            <div className="w-full flex justify-center mt-3">
              <Button
                className="btn-primary bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white rounded-xl px-6 py-2.5 shadow-lg"
                onClick={() => setPopUpModel(false)}
              >
                {t('common:generalSuccess.buttonText')}
              </Button>
            </div>
          }
        />
      )}

      {/* Page Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center shadow-sm">
          <svg
            className="w-6 h-6 text-orange dark:text-teal-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {displayText[DisplayTextKeys.TITLE_TEXT]}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {displayText[DisplayTextKeys.SUBTITLE_TEXT]}
          </p>
        </div>
      </div>

      {/* Contact Form Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm dark:shadow-slate-900/50 overflow-visible">
        {/* Card Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/10 dark:from-blue-400/20 dark:to-indigo-400/10 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('account:contactUs.form_title', 'Send us a message')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('account:contactUs.form_subtitle', "Fill out the form below and we'll get back to you")}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          <DisplayForm sections={sections} model={{ ...model, modelType: 'ContactUs' }} />

          {/* Submit Button */}
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700">
            <Button
              isProcessing={isSendingRequest}
              isDisable={isSendingRequest}
              className={`rounded-xl px-8 py-3 font-medium transition-all flex items-center gap-2 ${
                isSendingRequest
                  ? 'btn-disabled bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl hover:shadow-orange/30 dark:hover:shadow-teal-500/30'
              }`}
              onClick={onSendRequest}
              props={{
                name: displayText[DisplayTextKeys.SEND_REQUEST_TEXT],
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              {displayText[DisplayTextKeys.SEND_REQUEST_TEXT]}
            </Button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6 bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-slate-800/50 dark:to-slate-700/30 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/10 dark:from-green-400/20 dark:to-emerald-400/10 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('account:contactUs.help_title', 'Need immediate assistance?')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t(
                'account:contactUs.help_text',
                'Our team typically responds within 24 hours during business days. For urgent matters, please select the appropriate subject category.',
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
